import React, { useState, useRef, useEffect } from 'react';
import Typography from '@src/new-components/Typography';
import { Box, IconButton, Skeleton, CircularProgress } from '@mui/material';
import CommonButton from '@src/new-components/Button';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { COLOR } from '@src/styles/color';
import BadgeAvatar from '@src/components/BadgeAvatar';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import { useTranslation } from 'react-i18next';
import apis from '@src/apis';
import { useSelector, useDispatch } from 'react-redux';
import NoData from '@src/components/NoData';
import actions from '@src/redux/actions';
import { MAX_SPEAKER_FREE_LIMIT, MAX_SPEAKERS } from '@src/constants/project';
import sanitize from 'sanitize-html';
import LimitedFeature from '@src/components/LimitedFeature';
import Tooltip from '@src/components/Tooltip';
import { convertFormatDate } from '@src/utils/date';
import RunningWithErrorsRoundedIcon from '@mui/icons-material/RunningWithErrorsRounded';
import moment from 'moment';
import {
  SUB_STATUS,
  VOICE_NAME_REGEX,
  VOICE_STATUS,
} from '@src/constants/voiceCloning';
import { FREE_PACKAGE_CODES } from '@src/constants/package';
import route from '@src/constants/route';
import CrownImg from '@src/assets/images/crown.png';
import { useHistory } from 'react-router-dom';
import useForm from '@src/hooks/useForm';
import { createValidationSchema } from '@src/utils/form';
import { ERROR_CODE } from '@src/errors/code';
import VoiceSelectionDialog from '@src/containers/VoiceSelectionDialog';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import AddSpeakerForm from '../AddSpeaker/AddSpeakerForm';
import AddSpeakerActions from '../AddSpeaker/AddSpeakerActions';
import DialogHeaderWithBack from './DialogHeaderWithBack';
import {
  StyledListVoice,
  StyledVoiceItem,
  StyledVoiceList,
  StyledSpeakerItemWrapper,
  StyledErrorMessage,
  StyledErrorMessageWrapper,
} from './index.style';

const SPEAKER_SKELETON_KEYS = ['1', '2', '3'];

const SpeakerSettings = ({ onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projectId, multiBlock } = useSelector((state) => state.project);
  const { user } = useSelector((state) => state.auth);
  const usingFreePackage = FREE_PACKAGE_CODES.includes(user?.packageCode);
  const history = useHistory();
  const { blocks } = multiBlock || {};
  const speakers = useSelector((state) => state.project.speakers);
  const [currentVoiceEdit, setCurrentVoiceEdit] = useState(null);
  const [playingVoiceId, setPlayingVoiceId] = useState(null);
  const [loadingSpeakers, setLoadingSpeakers] = useState(false);
  const [speakerWithError, setSpeakerWithError] = useState(null);
  const [deletingSpeakerId, setDeletingSpeakerId] = useState(null);
  const [isEditView, setIsEditView] = useState(false);
  const [openVoices, setOpenVoices] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isApply = useRef(false);
  const errorMessageRefs = useRef({});
  const handleCloseWhenCreateVoice = () => {
    setCurrentVoiceEdit(null);
    onClose();
  };
  const maxSpeakersLimit = usingFreePackage
    ? MAX_SPEAKER_FREE_LIMIT
    : MAX_SPEAKERS;

  const validationSchema = createValidationSchema()
    .required('name', t('fieldIsRequired', { fieldName: t('speakerName') }))
    .minLength('name', 5, t('speakerNameDescription'))
    .pattern('name', VOICE_NAME_REGEX, t('speakerNameDescription'))
    .maxLength('name', 25, t('speakerNameDescription'))
    .required('voice', t('fieldIsRequired', { fieldName: t('voice') }))
    .required('speed', t('fieldIsRequired', { fieldName: t('voiceSpeed') }))
    .toObject();

  const {
    values: formValues,
    errors: formErrors,
    handleSubmit: handleFormSubmit,
    setFieldValue,
    resetForm,
    setValues,
    handleBlur,
    setFieldError,
  } = useForm({
    initialValues: {
      name: '',
      voice: null,
      speed: 1,
    },
    options: {
      resetOnSubmit: false,
    },
    onSubmit: async (value) => {
      setIsLoading(true);
      try {
        let response;
        if (currentVoiceEdit?.id) {
          response = await apis.project.updateSpeaker(currentVoiceEdit?.id, {
            name: value.name?.trim(),
            voiceCode: value.voice.code,
            speed: value.speed,
          });
        } else {
          response = await apis.project.createSpeaker({
            name: value.name?.trim(),
            voiceCode: value.voice.code,
            speed: value.speed,
            projectId,
          });
        }
        if (response?.status) {
          const newSpeaker = response?.result?.speaker;
          if (newSpeaker && !currentVoiceEdit?.id) {
            dispatch(actions.project.addSpeaker({ ...value, ...newSpeaker }));
          } else if (currentVoiceEdit?.id) {
            dispatch(
              actions.project.updateSpeaker(currentVoiceEdit?.id, {
                ...value,
                ...newSpeaker,
              }),
            );
            const blocksUsingSpeaker = Object.keys(blocks || {}).filter(
              (id) => blocks[id]?.speakerId === currentVoiceEdit?.id,
            );
            // Batch update all blocks at once to avoid multiple re-renders
            if (blocksUsingSpeaker?.length > 0) {
              const blockUpdates = blocksUsingSpeaker.map(
                (blockIdToUpdate) => ({
                  blockId: blockIdToUpdate,
                  block: {
                    voiceCode: value?.voice?.code,
                    speed: value?.speed,
                    speakerId: newSpeaker?.id,
                    voice: value?.voice || {},
                  },
                }),
              );
              dispatch(actions.project.updateMultipleBlocks(blockUpdates));
            }
          }
          // Refresh speakers list
          const refreshResponse = await apis.project.getSpeakers(projectId);
          if (refreshResponse?.status) {
            dispatch(
              actions.project.setSpeakers(
                refreshResponse?.result?.speakers || [],
              ),
            );
          }
          resetForm();
          setIsEditView(false);
          setCurrentVoiceEdit(null);
          dispatch(
            actions.noti.push({
              severity: 'success',
              message: t('saveProjectSuccessful'),
            }),
          );
        } else if (response?.errorCode === ERROR_CODE.SPEAKER_NAME_EXISTED) {
          setFieldError('name', t('speakerNameExisted'));
        }
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        dispatch(
          actions.noti.push({
            severity: 'error',
            message: t('errorOccurred'),
          }),
        );
      }
    },
    validationSchema,
  });

  useEffect(() => {
    if (currentVoiceEdit && isEditView) {
      setValues({
        name: currentVoiceEdit?.name,
        voice: currentVoiceEdit?.voice,
        speed: currentVoiceEdit?.speed,
      });
    } else if (isEditView && !currentVoiceEdit) {
      // Reset form when creating new speaker
      resetForm();
    }
  }, [currentVoiceEdit, isEditView, setValues, resetForm]);

  useEffect(() => {
    const fetchSpeakers = async () => {
      if (!projectId) return;
      setLoadingSpeakers(true);
      try {
        const response = await apis.project.getSpeakers(projectId);
        if (response?.status) {
          dispatch(
            actions.project.setSpeakers(response?.result?.speakers || []),
          );
        }
      } finally {
        setLoadingSpeakers(false);
      }
    };
    fetchSpeakers();
  }, [projectId, dispatch]);

  const audioRef = useRef(null);
  const pausedPositionRef = useRef({});

  const handlePlay = (item) => {
    if (playingVoiceId === item.id && audioRef.current) {
      pausedPositionRef.current[item.id] = audioRef.current.currentTime;
      audioRef.current.pause();
      setPlayingVoiceId(null);
      return;
    }

    if (audioRef.current && playingVoiceId !== item.id) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
      if (playingVoiceId) {
        pausedPositionRef.current[playingVoiceId] = 0;
      }
    }

    if (item?.voice?.demo) {
      const audio = new window.Audio(item?.voice?.demo);
      audioRef.current = audio;

      if (pausedPositionRef.current[item.id]) {
        audio.currentTime = pausedPositionRef.current[item.id];
      }

      audio.play();
      setPlayingVoiceId(item.id);

      audio.onended = () => {
        setPlayingVoiceId(null);
        audioRef.current = null;
        pausedPositionRef.current[item.id] = 0;
      };
      audio.onerror = () => {
        setPlayingVoiceId(null);
        audioRef.current = null;
        pausedPositionRef.current[item.id] = 0;
      };
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    if (playingVoiceId) {
      pausedPositionRef.current[playingVoiceId] = 0;
    }
    setPlayingVoiceId(null);
  };

  React.useEffect(
    () => () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      // Reset tất cả vị trí đã lưu khi component unmount
      pausedPositionRef.current = {};
    },
    [],
  );

  const handleDeleteSpeaker = async (speaker) => {
    stopAudio();
    const hasBlockUsingSpeaker = Object.values(blocks)?.some(
      (block) => block?.speakerId === speaker?.id,
    );
    if (hasBlockUsingSpeaker) {
      setSpeakerWithError(speaker.id);
      // Scroll to error message after state update and DOM render
      setTimeout(() => {
        const errorElement = errorMessageRefs.current[speaker.id];
        if (errorElement) {
          errorElement.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
            inline: 'nearest',
          });
        }
      }, 150);
      return;
    }
    setSpeakerWithError(null);
    setDeletingSpeakerId(speaker.id);
    const response = await apis.project.deleteSpeaker(speaker.id);
    if (response?.status) {
      dispatch(actions.project.deleteSpeaker(speaker.id));
      setSpeakerWithError(null);
    }
    setDeletingSpeakerId(null);
  };

  const handleOpenAddSpeaker = () => {
    stopAudio();
    if (speakers.length >= maxSpeakersLimit && usingFreePackage) {
      dispatch(
        actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
          description: t('limitedFeatureAddSpeaker'),
          img: CrownImg,
          classNameImg: 'custom-img',
          maxWidth: '512px',
          showBlurBackground: true,
          onUpgrade: () => {
            history.push(route.PAYMENT);
            onClose();
            dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
          },
        }),
      );
      return;
    }
    setCurrentVoiceEdit(null);
    setIsEditView(true);
    resetForm();
  };

  const handleBackToList = () => {
    setIsEditView(false);
    setCurrentVoiceEdit(null);
    setSpeakerWithError(null);
    resetForm();
  };

  const handleCloseDialog = (event, reason) => {
    if (reason === 'backdropClick') return;
    onClose(event, reason);
  };

  const handleChangeVoice = (newVoice) => setFieldValue('voice', newVoice);

  const handleFieldChange = (field, value) => {
    setFieldValue(field, value);
  };

  const handleFieldBlur = (field) => {
    handleBlur(field);
  };

  const handleSave = () => {
    isApply.current = false;
    handleFormSubmit();
  };

  const handleCloseVoices = () => setOpenVoices(false);

  const renderSpeakersList = () => {
    if (loadingSpeakers) {
      return SPEAKER_SKELETON_KEYS.map((key) => (
        <StyledVoiceItem key={`speaker-skeleton-${key}`}>
          <Box display="flex" gap="8px" alignItems="center">
            <Skeleton variant="circular" width={25} height={25} />
            <Box display="flex" flexDirection="column" gap="6px">
              <Skeleton variant="text" width={140} height={18} />
              <Skeleton variant="text" width={180} height={16} />
            </Box>
          </Box>
          <Box display="flex" gap="8px" alignItems="center">
            <Skeleton variant="circular" width={32} height={32} />
            <Skeleton variant="circular" width={32} height={32} />
            <Skeleton variant="circular" width={32} height={32} />
          </Box>
        </StyledVoiceItem>
      ));
    }

    if (!speakers?.length) {
      return (
        <Box display="flex" justifyContent="center" py={1}>
          <NoData dropTable noDataText={t('noData')} />
        </Box>
      );
    }

    return speakers.map((item) => {
      const voiceInActive = !item?.voice?.active;
      const isInValidPublicVoiceCloning =
        (item?.voice?.userId !== user?.id &&
          ((item?.voice?.discardAt &&
            moment().isAfter(item?.voice?.discardAt)) ||
            item?.voice?.status === VOICE_STATUS.PRIVATE)) ||
        voiceInActive;

      const isPublicVoiceCloning = item?.voice?.status === SUB_STATUS.PUBLIC;
      const isVoiceCloningAboutToRemoved =
        isPublicVoiceCloning && item?.voice?.discardAt
          ? moment().isBefore(item?.voice?.discardAt)
          : false;
      const hasError = speakerWithError === item.id;
      const hasBlockUsingSpeaker = Object.values(blocks)?.some(
        (block) => block?.speakerId === item?.id,
      );
      return (
        <StyledSpeakerItemWrapper key={item.id || item}>
          <StyledVoiceItem>
            <Box display="flex" gap="8px" alignItems="center">
              <BadgeAvatar
                width={25}
                smallImgWidth={10}
                img={item?.voice?.roundImage}
                smallImg={item?.voice?.language?.roundImage}
                type="image"
              />
              <Box display="flex" flexDirection="column" gap="4px">
                <Typography
                  variant="body-14-regular"
                  color={COLOR.descriptionGray}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  {item?.name || t('defaultSpeakerName')}
                  <LimitedFeature voiceFeatures={item?.voice?.features} />
                  {isVoiceCloningAboutToRemoved && (
                    <Tooltip
                      arrow
                      title={t('voiceCloningDiscardAt', {
                        date: convertFormatDate(item?.voice?.discardAt),
                      })}
                      placement="top"
                    >
                      <RunningWithErrorsRoundedIcon className="returning-error-icon" />
                    </Tooltip>
                  )}
                  {isInValidPublicVoiceCloning && (
                    <Tooltip
                      arrow
                      title={t('invalidPublicVoiceCloning')}
                      placement="top"
                    >
                      <WarningRoundedIcon className="returning-error-icon" />
                    </Tooltip>
                  )}
                </Typography>
                <Typography
                  variant="body-12-regular"
                  color={COLOR.descriptionUpload}
                >
                  {item?.voice?.name} ({t('voiceSpeed')}: {item?.speed}x)
                </Typography>
              </Box>
            </Box>
            <Box display="flex" gap="8px" alignItems="center">
              <IconButton
                onClick={() => {
                  handlePlay(item);
                }}
                disabled={isInValidPublicVoiceCloning}
                aria-label={
                  playingVoiceId === item.id
                    ? 'Pause demo audio'
                    : 'Play demo audio'
                }
                sx={{
                  cursor: isInValidPublicVoiceCloning
                    ? 'not-allowed'
                    : 'pointer',
                  backgroundColor: isInValidPublicVoiceCloning && 'transparent',
                  opacity: isInValidPublicVoiceCloning ? 0.5 : 1,
                }}
              >
                {playingVoiceId === item.id ? (
                  <PauseRoundedIcon
                    sx={{ fontSize: 24, color: COLOR.navyBlue }}
                  />
                ) : (
                  <PlayArrowRoundedIcon
                    sx={{ fontSize: 24, color: COLOR.navyBlue }}
                  />
                )}
              </IconButton>
              <IconButton
                onClick={() => {
                  stopAudio();
                  setCurrentVoiceEdit(item);
                  setIsEditView(true);
                  setSpeakerWithError(null);
                }}
              >
                <EditRoundedIcon sx={{ fontSize: 24, color: COLOR.navyBlue }} />
              </IconButton>
              <IconButton
                onClick={() => handleDeleteSpeaker(item)}
                disabled={deletingSpeakerId === item.id}
              >
                {deletingSpeakerId === item.id ? (
                  <CircularProgress
                    size={24}
                    thickness={5}
                    sx={{ color: COLOR.navyBlue }}
                  />
                ) : (
                  <DeleteRoundedIcon
                    sx={{ fontSize: 24, color: COLOR.navyBlue }}
                  />
                )}
              </IconButton>
            </Box>
          </StyledVoiceItem>
          {hasError && hasBlockUsingSpeaker && (
            <StyledErrorMessageWrapper
              ref={(el) => {
                if (el) {
                  errorMessageRefs.current[item.id] = el;
                }
              }}
            >
              <StyledErrorMessage variant="body-12-regular">
                {t('cannotDeleteSpeakerInline')}
              </StyledErrorMessage>
            </StyledErrorMessageWrapper>
          )}
        </StyledSpeakerItemWrapper>
      );
    });
  };

  const renderContent = () => {
    if (isEditView) {
      return (
        <AddSpeakerForm
          values={formValues}
          errors={formErrors}
          isEdit={!!currentVoiceEdit}
          user={user}
          onFieldChange={handleFieldChange}
          onFieldBlur={handleFieldBlur}
          onOpenVoices={() => setOpenVoices(true)}
          t={t}
        />
      );
    }
    return (
      <StyledListVoice>
        {speakers?.length > 0 && (
          <Typography
            variant="body-16-regular"
            dangerouslySetInnerHTML={{
              __html: sanitize(
                t('titleListVoice', {
                  speakerCount: speakers.length,
                  maxSpeakers: maxSpeakersLimit,
                }),
                {
                  allowedTags: ['strong'],
                },
              ),
            }}
          />
        )}
        <StyledVoiceList className="scrollbar-custom">
          {renderSpeakersList()}
        </StyledVoiceList>
      </StyledListVoice>
    );
  };

  const renderActionComponent = () => {
    if (isEditView) {
      return (
        <AddSpeakerActions
          isEdit={!!currentVoiceEdit}
          isApplyPreset={false}
          isLoading={isLoading}
          isApplying={isApply.current}
          onCancel={handleBackToList}
          onSave={handleSave}
          onSaveAndApply={() => {}}
          t={t}
        />
      );
    }
    return (
      <CommonButton
        variant="outlineSmall"
        textProps={{
          variantText: 'body-14-medium',
          color: COLOR.darkBlue,
        }}
        disabled={speakers.length >= maxSpeakersLimit && !usingFreePackage}
        customStyle={{
          height: '40px',
          borderRadius: '8px',
          padding: '8px 8px',
          width: '157px',
        }}
        startIcon={
          <AddRoundedIcon sx={{ fontSize: 24, color: COLOR.navyBlue }} />
        }
        onClick={handleOpenAddSpeaker}
      >
        {t('addSpeaker')}
      </CommonButton>
    );
  };

  const renderTitle = () => {
    if (isEditView) {
      return (
        <DialogHeaderWithBack
          title={
            currentVoiceEdit ? t('editSpeakerConfiguration') : t('addSpeaker')
          }
          onBack={handleBackToList}
        />
      );
    }
    return null;
  };

  return (
    <>
      <CommonDialog
        width="640px"
        fullWidth
        open
        onClose={handleCloseDialog}
        title={isEditView ? undefined : t('speakerSettings')}
        renderTitle={renderTitle()}
        contentComponent={renderContent()}
        actionComponent={renderActionComponent()}
      />
      {openVoices && (
        <VoiceSelectionDialog
          open={openVoices}
          activeVoiceId={formValues.voice?.id}
          activeVoiceCode={formValues.voice?.code}
          onClose={handleCloseVoices}
          onChangeVoice={handleChangeVoice}
          hasLanguageFilter
          isApplyPreset
          voiceStatus={formValues.voice?.status}
          onCloseWhenCreateVoice={handleCloseWhenCreateVoice}
          isDubbing
          multiLanguage
        />
      )}
    </>
  );
};

export default SpeakerSettings;
